import { Capacitor } from '@capacitor/core'
import { Camera, CameraDirection, CameraResultType, CameraSource } from '@capacitor/camera'
import { ensureCameraPermission } from './nativePermissions'

function nativeCameraAvailable(): boolean {
  return Capacitor.isPluginAvailable('Camera')
}

function pickWithCaptureInput(facing: 'front' | 'rear'): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = facing === 'front' ? 'user' : 'environment'
    input.style.display = 'none'
    const cleanup = () => {
      input.remove()
    }
    input.addEventListener('change', () => {
      const file = input.files?.[0]
      cleanup()
      if (file) resolve(file)
      else reject(new Error('No photo captured'))
    })
    input.addEventListener('cancel', () => {
      cleanup()
      reject(new Error('Camera cancelled'))
    })
    document.body.appendChild(input)
    input.click()
  })
}

/** Capture a photo from the device camera and return it as a File for crop/upload. */
export async function capturePhotoFile(opts?: {
  facing?: 'front' | 'rear'
}): Promise<File> {
  const facing = opts?.facing ?? 'rear'
  const allowed = await ensureCameraPermission()
  if (!allowed) {
    throw new Error('Allow camera access in Settings to take a photo')
  }

  if (nativeCameraAvailable()) {
    const photo = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
      direction: facing === 'front' ? CameraDirection.Front : CameraDirection.Rear,
      correctOrientation: true,
    })
    if (!photo.webPath) throw new Error('No photo captured')
    const res = await fetch(photo.webPath)
    const blob = await res.blob()
    const type = blob.type || 'image/jpeg'
    const ext = type.includes('png') ? 'png' : 'jpg'
    return new File([blob], `capture-${Date.now()}.${ext}`, { type })
  }

  return pickWithCaptureInput(facing)
}
